import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, User, UserRoundPlus, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/library/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAppSelector } from "@/stores/hook";
import { selectCurrentUserId } from "@/features/auth/currentUser";
import { alertTimeAgo } from "@/features/alerts/types";
import {
  assignmentRequestErrorMessage,
  useAssignmentRequestOptions,
  useCreateAssignmentRequest,
} from "@/features/leads/api/useAssignmentRequests";

const NOTE_MAX = 1000;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: number | null;
  /** The active deal, when the request is about a deal rather than the lead. */
  childId: number | null;
}

type TargetKind = "role" | "user";

/**
 * "Request an assignment" (owner, 2026-11-02): for someone who cannot assign,
 * or wants a role above them to decide. Ask a role (everyone in it who can
 * assign, narrowed to this lead's branch) or one person; suggest who should
 * get the lead, or leave it to them; add a note. It lands as an announcement
 * that pops up on their screen until Done — or until the lead is assigned.
 *
 * The form lives inside DialogContent, which Radix unmounts on close, so
 * every opening starts from a fresh form without a reset effect.
 */
export default function AssignRequestDialog({ open, onOpenChange, leadId, childId }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* minmax(0,1fr): the one grid column must not grow to its longest unwrapped line (see AlertComposeDialog). */}
      <DialogContent className="sm:max-w-[520px] grid-cols-[minmax(0,1fr)] rounded-xl p-0 gap-0 overflow-hidden">
        <AssignRequestForm leadId={leadId} childId={childId} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function AssignRequestForm({
  leadId,
  childId,
  onClose,
}: {
  leadId: number | null;
  childId: number | null;
  onClose: () => void;
}) {
  const [pickedKind, setPickedKind] = useState<TargetKind | null>(null);
  const [targetId, setTargetId] = useState("");
  const [suggestedId, setSuggestedId] = useState("");
  const [note, setNote] = useState("");

  const options = useAssignmentRequestOptions(leadId, childId, true);
  const create = useCreateAssignmentRequest();
  const currentUserId = useAppSelector(selectCurrentUserId);

  const data = options.data;
  const subject = data?.lead.dealName ? "deal" : "lead";

  // Roles by default; a person when nothing can be asked as a role.
  const kind: TargetKind =
    pickedKind ?? (data && data.roles.length === 0 && data.people.length > 0 ? "user" : "role");
  const setKind = (next: TargetKind) => {
    setPickedKind(next);
    setTargetId("");
  };

  const roleOptions = useMemo(
    () =>
      (data?.roles ?? []).map((r) => ({
        value: String(r.id),
        label: r.name,
        sublabel: `${r.userCount} ${r.userCount === 1 ? "person" : "people"} would be asked`,
      })),
    [data?.roles],
  );
  const peopleOptions = useMemo(
    () =>
      (data?.people ?? []).map((p) => ({
        value: String(p.id),
        label: p.name ?? `User #${p.id}`,
        sublabel: p.roleName ?? null,
      })),
    [data?.people],
  );
  const assigneeOptions = useMemo(
    () =>
      (data?.assignees ?? []).map((p) => ({
        value: String(p.id),
        label: p.name ?? `User #${p.id}`,
        sublabel: p.roleName ?? null,
      })),
    [data?.assignees],
  );

  const mine = (data?.pending ?? []).find(
    (r) =>
      Number(r.requestedBy.id) === Number(currentUserId) &&
      (childId ? r.childId === childId : r.childId == null),
  );

  const reach = useMemo(() => {
    if (!data || !targetId) return null;
    if (kind === "role") {
      const r = data.roles.find((x) => String(x.id) === targetId);
      return r ? `Goes to ${r.userCount} ${r.userCount === 1 ? "person" : "people"} in ${r.name}` : null;
    }
    const p = data.people.find((x) => String(x.id) === targetId);
    return p ? `Goes to ${p.name ?? "them"}` : null;
  }, [data, kind, targetId]);

  const canSend = !!leadId && !!targetId && !mine && !create.isPending;

  const send = async () => {
    if (!leadId || !targetId) return;
    try {
      const result = await create.mutateAsync({
        leadId,
        childId: childId ?? null,
        targetRoleId: kind === "role" ? Number(targetId) : null,
        targetUserId: kind === "user" ? Number(targetId) : null,
        suggestedUserId: suggestedId ? Number(suggestedId) : null,
        note: note.trim() || null,
      });
      const names = result?.sample ?? [];
      const extra = (result?.recipientCount ?? names.length) - names.length;
      toast.success(
        `Request sent to ${names.join(", ")}${extra > 0 ? ` +${extra}` : ""}. It pops up for them until it's done.`,
      );
      onClose();
    } catch (err) {
      toast.error(assignmentRequestErrorMessage(err, "Could not send the request"));
    }
  };

  return (
    <>
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[var(--color-black-1)]">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-[#051912]">
            <span className="flex size-8 items-center justify-center rounded-full bg-amber-50">
              <UserRoundPlus className="size-4 text-amber-600" />
            </span>
            Request an assignment
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[#697571]">
            Ask someone who can assign to put this {subject} with the right person. It pops up on
            their screen until it is done.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 flex flex-col gap-4 max-h-[65vh] overflow-y-auto">
          {data?.lead && (
            <p className="m-0 text-[12.5px] text-[var(--color-black-7)]">
              <span className="font-semibold text-[var(--color-black-9)]">{data.lead.name}</span>
              {data.lead.dealName ? (
                <>
                  {" "}
                  · deal <span className="font-medium">{data.lead.dealName}</span>
                </>
              ) : null}
            </p>
          )}

          {mine && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11.5px] text-amber-800">
              You already asked <span className="font-semibold">{mine.target.name}</span>{" "}
              {alertTimeAgo(mine.createdAt).toLowerCase()}. It is still waiting — cancel it from the
              chip next to the assignees to ask again.
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label>Ask</Label>
            <div className="grid grid-cols-2 gap-2">
              <KindCard
                active={kind === "role"}
                onClick={() => setKind("role")}
                icon={<Users className="size-4" />}
                title="A role"
                text="Everyone in it who can assign, in this lead's branch."
                disabled={!!data && data.roles.length === 0}
              />
              <KindCard
                active={kind === "user"}
                onClick={() => setKind("user")}
                icon={<User className="size-4" />}
                title="A person"
                text="One person who can assign."
                disabled={!!data && data.people.length === 0}
              />
            </div>
            <SearchPicker
              value={targetId}
              onChange={setTargetId}
              options={kind === "role" ? roleOptions : peopleOptions}
              placeholder={kind === "role" ? "Pick a role" : "Pick a person"}
              searchPlaceholder={kind === "role" ? "Search roles…" : "Search people…"}
              emptyLabel={
                options.isLoading
                  ? "Loading…"
                  : kind === "role"
                    ? "No role here can assign"
                    : "Nobody here can assign"
              }
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>
              Assign to{" "}
              <span className="font-normal text-[var(--color-black-4)]">· optional</span>
            </Label>
            <SearchPicker
              value={suggestedId}
              onChange={setSuggestedId}
              options={assigneeOptions}
              placeholder="Let them decide"
              searchPlaceholder="Search people…"
              emptyLabel={options.isLoading ? "Loading…" : "Nobody found"}
              noneLabel="Let them decide"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="assign-request-note">
                Note{" "}
                <span className="font-normal text-[var(--color-black-4)]">· optional</span>
              </Label>
              <span className="text-[10.5px] text-[var(--color-black-4)]">
                {note.length}/{NOTE_MAX}
              </span>
            </div>
            <Textarea
              id="assign-request-note"
              value={note}
              maxLength={NOTE_MAX}
              rows={3}
              placeholder="Why, or anything they should know"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-[var(--color-black-1)] flex items-center justify-between gap-3 sm:justify-between">
          <div className="min-w-0 text-[11.5px] text-[var(--color-black-6)] truncate">
            {reach ?? (options.isLoading ? <span className="animate-pulse">Loading…</span> : "")}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button variant="primary" onClick={send} disabled={!canSend}>
              {create.isPending ? "Sending…" : "Send request"}
            </Button>
          </div>
        </DialogFooter>
    </>
  );
}

function KindCard({
  active,
  onClick,
  icon,
  title,
  text,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  text: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      disabled={disabled}
      className={cn(
        "flex items-start gap-2.5 rounded-xl border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        active
          ? "border-amber-400 bg-amber-50/60"
          : "border-[var(--color-black-2)] hover:bg-[var(--color-black-1)]/60",
      )}
    >
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-[12.5px] font-semibold text-[var(--color-black-9)]">{title}</span>
        <span className="block mt-0.5 text-[11px] leading-snug text-[var(--color-black-6)]">{text}</span>
      </span>
    </button>
  );
}

interface PickerOption {
  value: string;
  label: string;
  sublabel?: string | null;
}

const MAX_VISIBLE = 60;

/** Searchable single-select (Popover + cmdk), the HR picker's shape in the leads palette. */
function SearchPicker({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  noneLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
  noneLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.sublabel ?? "").toLowerCase().includes(q),
    );
  }, [options, search]);
  const visible = filtered.slice(0, MAX_VISIBLE);
  const hidden = filtered.length - visible.length;

  const selected = options.find((o) => o.value === value);

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-9 w-full items-center justify-between gap-2 rounded-[10px] border border-[var(--color-black-2)] bg-white px-3 text-left text-[12.5px] outline-none transition-colors",
            "hover:bg-[var(--color-black-1)]/40 focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/30",
          )}
        >
          <span className={cn("truncate", selected ? "text-[var(--color-black-9)]" : "text-[var(--color-black-4)]")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[300] flex w-(--radix-popover-trigger-width) min-w-[16rem] flex-col overflow-hidden bg-white p-0"
        style={{ maxHeight: "min(50vh, 20rem)" }}
      >
        <Command shouldFilter={false} className="flex min-h-0 flex-1 flex-col">
          <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
          <CommandList className="max-h-none min-h-0 flex-1">
            {visible.length === 0 && !noneLabel && (
              <CommandEmpty className="py-6 text-center text-[12px] text-[var(--color-black-5)]">
                {emptyLabel}
              </CommandEmpty>
            )}
            <CommandGroup>
              {noneLabel && (
                <CommandItem value="__none__" onSelect={() => choose("")}>
                  <Check className={cn("mr-1 size-4 shrink-0", value === "" ? "opacity-100" : "opacity-0")} />
                  <span className="text-[var(--color-black-5)]">{noneLabel}</span>
                </CommandItem>
              )}
              {visible.map((o) => (
                <CommandItem key={o.value} value={o.value} onSelect={() => choose(o.value)} className="items-center">
                  <Check className={cn("mr-1 size-4 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{o.label}</span>
                    {o.sublabel && (
                      <span className="truncate text-[10.5px] text-[var(--color-black-5)]">{o.sublabel}</span>
                    )}
                  </span>
                </CommandItem>
              ))}
              {hidden > 0 && (
                <div className="px-2 py-1.5 text-[10.5px] text-[var(--color-black-4)]">
                  {hidden} more — keep typing to narrow it down
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
